import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { ArrowLeft, CalendarClock, ChevronDown } from 'lucide-react';
import { isReportId, type ReportId } from '@gc/shared';

import { useAuth } from '@/features/auth/useAuth';
import {
  CATEGORY_PARAM,
  categoryIdsForRequest,
  normaliseCategorySelection,
  parseCategoryParam,
  REPORT_UNCATEGORISED_ID,
  serialiseCategoryParam,
  showsCategoryFilter,
  type CategoryOption,
  type CategorySelection,
} from './categorySelection';
import { LegacyReportView } from './LegacyReportView';
import { CategoryFilterChip, CategoryFilterPanel } from './ReportCategoryFilter';
import { ReportRangePicker } from './ReportRangePicker';
import { ReportTableView } from './ReportTableView';
import {
  matchPreset,
  rangeFromBusinessDay,
  rangeFromPreset,
  useReportCatalogue,
  useReportCategoryOptions,
  useReportTable,
  type ReportPreset,
  type ReportRangeState,
} from './useReports';

/** Where the picker's "Last viewed" shortcut reads from. */
export const LAST_REPORT_KEY = 'gc.reports.last';

/** How long ticking waits before asking the server — five quick ticks, one fetch. */
const CATEGORY_FETCH_DELAY_MS = 350;

/**
 * One report, on its own page (owner: "take me to a different page where i
 * can see the report and there's will just be a simple back arrow").
 *
 * The chrome is exactly that: a back arrow, one slim range chip, and the
 * sheet. The DATA owns the screen (owner: "i want the data to be as clear
 * as possible") — the presets and From/To live folded behind the chip, and
 * fold back the moment a fetch or a preset lands, so controls exist only
 * while they are being used. The sheet titles itself — both viewers carry
 * their own heading — so this page adds no second header to scroll past.
 * Archived reports get no range chip at all: they are the old system's own
 * figures for one fixed period, not a query.
 *
 * Reached at `/reports/:reportId` (live) or `/reports/archived/:legacyId`.
 * An id the server no longer offers bounces back to the picker rather than
 * presenting an empty sheet as if it were data.
 */
export function ReportViewPage() {
  const { t } = useTranslation();
  const { can } = useAuth();
  const navigate = useNavigate();
  const { reportId, legacyId } = useParams();

  const allowed = can('reports.view');
  const { catalogue, error: catalogueError } = useReportCatalogue();

  const liveId: ReportId | null = !legacyId && reportId && isReportId(reportId) ? reportId : null;

  const [draftRange, setDraftRange] = useState<ReportRangeState | null>(null);
  const [appliedRange, setAppliedRange] = useState<ReportRangeState | null>(null);
  /** The range controls, folded behind the chip until asked for. */
  const [rangeOpen, setRangeOpen] = useState(false);
  /** The category picker, folded the same way. */
  const [categoriesOpen, setCategoriesOpen] = useState(false);

  /*
   * The category filter (owner, 2026-09-23), on the reports that honour it.
   * The selection is read from the URL on every render — `?categories=a,b` —
   * so a refresh or a shared link opens on the same categories.
   */
  const [searchParams, setSearchParams] = useSearchParams();
  const filterable = liveId !== null && showsCategoryFilter(liveId);
  const { categories, error: categoriesError } = useReportCategoryOptions(filterable);
  const categoryOptions = useMemo<CategoryOption[]>(
    () =>
      categories
        ? [
            ...categories,
            { id: REPORT_UNCATEGORISED_ID, name: t('reports.categoryFilter.uncategorised') },
          ]
        : [],
    [categories, t],
  );
  const categoryParam = filterable ? searchParams.get(CATEGORY_PARAM) : null;
  const categorySelection = normaliseCategorySelection(
    parseCategoryParam(categoryParam),
    categoryOptions,
  );
  const categoryKey = serialiseCategoryParam(categorySelection);

  const setCategorySelection = useCallback(
    (next: CategorySelection) => {
      const value = serialiseCategoryParam(next);
      // Replace, not push: Back leaves the report instead of un-ticking boxes.
      setSearchParams(
        (current) => {
          const params = new URLSearchParams(current);
          if (value === null) params.delete(CATEGORY_PARAM);
          else params.set(CATEGORY_PARAM, value);
          return params;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  // Once the menu is known, rewrite a link naming deleted categories (or
  // every category) to its canonical form, so the chip and the URL agree.
  useEffect(() => {
    if (!filterable || !categories) return;
    if (categoryKey !== categoryParam) setCategorySelection(parseCategoryParam(categoryKey));
  }, [filterable, categories, categoryKey, categoryParam, setCategorySelection]);

  // What the server is asked for trails the ticks by a beat.
  const [requestKey, setRequestKey] = useState(categoryKey);
  useEffect(() => {
    if (requestKey === categoryKey) return;
    const timer = setTimeout(() => setRequestKey(categoryKey), CATEGORY_FETCH_DELAY_MS);
    return () => clearTimeout(timer);
  }, [categoryKey, requestKey]);
  const requestCategoryIds = filterable
    ? categoryIdsForRequest(parseCategoryParam(requestKey))
    : undefined;
  /** Nothing ticked: the server would read that as everything, so nothing is asked. */
  const nothingTicked = requestCategoryIds !== undefined && requestCategoryIds.length === 0;

  // A live id that is not in today's catalogue must bounce, not 404 quietly.
  useEffect(() => {
    if (legacyId || liveId) return;
    navigate('/reports', { replace: true });
  }, [legacyId, liveId, navigate]);

  // Remember what was actually OPENED — the picker's shortcut reads this.
  useEffect(() => {
    const choice = legacyId
      ? { kind: 'legacy' as const, id: legacyId }
      : liveId
        ? { kind: 'live' as const, id: liveId }
        : null;
    if (!choice) return;
    try {
      localStorage.setItem(LAST_REPORT_KEY, JSON.stringify(choice));
    } catch {
      // Private browsing — viewing still works, it just won't be remembered.
    }
  }, [legacyId, liveId]);

  // Seed both ranges from the venue's own business day once it is known.
  useEffect(() => {
    if (!catalogue || draftRange) return;
    const seeded = rangeFromBusinessDay(
      catalogue.range.fromBusinessDate,
      catalogue.range.openingTime,
      catalogue.range.closingTime,
    );
    setDraftRange(seeded);
    setAppliedRange(seeded);
  }, [catalogue, draftRange]);

  // A preset is a decision, not a draft — it fetches straight away, otherwise
  // tapping "All time" and seeing nothing change reads as another dead control.
  // A decision also FOLDS the controls: the next thing wanted is the data.
  const applyPreset = (preset: ReportPreset) => {
    if (!catalogue) return;
    const next = rangeFromPreset(
      preset,
      catalogue.range.fromBusinessDate,
      catalogue.range.openingTime,
      catalogue.range.closingTime,
      catalogue.earliestBusinessDate,
    );
    setDraftRange(next);
    setAppliedRange(next);
    setRangeOpen(false);
  };

  const fetchNow = () => {
    setAppliedRange(draftRange);
    setRangeOpen(false);
  };

  const { table, loading, error } = useReportTable(
    nothingTicked ? null : liveId,
    appliedRange,
    requestCategoryIds,
  );

  /**
   * What the chip says while folded: the matched preset's own name reads
   * clearer than two timestamps; otherwise the server-resolved business days,
   * falling back to the draft instants before the first fetch answers.
   */
  const appliedPreset = appliedRange && catalogue ? matchPreset(appliedRange, catalogue) : null;
  const rangeSummary = appliedPreset
    ? t(`reports.presets.${appliedPreset}`)
    : table
      ? `${table.range.fromBusinessDate} → ${table.range.toBusinessDate}`
      : appliedRange
        ? `${appliedRange.from.replace('T', ' ')} → ${appliedRange.to.replace('T', ' ')}`
        : '…';

  if (!allowed) {
    return (
      <div className="p-6">
        <h1 className="text-xl font-semibold text-content">{t('reports.title')}</h1>
        <p className="mt-2 text-sm text-content-muted">{t('reports.noViewPermission')}</p>
      </div>
    );
  }

  return (
    <div className="flex h-full flex-col">
      {/* Print scoping: the report table is what goes on paper, not the chrome. */}
      <style>{`
        @media print {
          body * { visibility: hidden !important; }
          .gc-report-print, .gc-report-print * { visibility: visible !important; }
          .gc-report-print {
            position: absolute !important;
            inset-inline-start: 0 !important;
            top: 0 !important;
            width: 100% !important;
          }
          .gc-no-print { display: none !important; }
        }
      `}</style>

      {/* The whole header is a back arrow and one range chip (plus the
          category chip on the reports that take it) — the sheet names
          itself, and the DATA gets everything below this line. */}
      <div className="gc-no-print flex flex-wrap items-center gap-2 px-4 pt-4">
        <button
          type="button"
          onClick={() => navigate('/reports')}
          title={t('reports.backToReports')}
          aria-label={t('reports.backToReports')}
          className="grid h-9 w-9 shrink-0 place-items-center rounded-full border border-subtle text-content-secondary transition-colors hover:bg-hover hover:text-content"
        >
          <ArrowLeft className="h-4 w-4 rtl:rotate-180" aria-hidden />
        </button>

        {liveId && draftRange ? (
          <button
            type="button"
            onClick={() => {
              setRangeOpen((open) => !open);
              setCategoriesOpen(false);
            }}
            aria-expanded={rangeOpen}
            className={`flex min-w-0 items-center gap-2 rounded-full border bg-input px-3.5 py-1.5 text-sm transition-colors ${
              rangeOpen
                ? 'border-primary text-content'
                : 'border-subtle text-content-secondary hover:border-strong hover:text-content'
            }`}
          >
            <CalendarClock className="h-3.5 w-3.5 shrink-0 text-content-muted" aria-hidden />
            <span className="tabular truncate">{rangeSummary}</span>
            <ChevronDown
              className={`h-3.5 w-3.5 shrink-0 text-content-secondary transition-transform ${
                rangeOpen ? 'rotate-180' : ''
              }`}
              aria-hidden
            />
          </button>
        ) : null}

        {filterable ? (
          <CategoryFilterChip
            selection={categorySelection}
            options={categoryOptions}
            open={categoriesOpen}
            onToggle={() => {
              setCategoriesOpen((open) => !open);
              setRangeOpen(false);
            }}
          />
        ) : null}
      </div>

      {liveId && draftRange && rangeOpen ? (
        <div className="gc-no-print px-4 pt-3">
          <ReportRangePicker
            value={draftRange}
            onChange={setDraftRange}
            onFetch={fetchNow}
            busy={loading}
            resolved={table?.range ?? null}
            onPreset={catalogue ? applyPreset : undefined}
            activePreset={catalogue ? matchPreset(draftRange, catalogue) : null}
          />
        </div>
      ) : null}

      {filterable && categoriesOpen ? (
        <div className="gc-no-print px-4 pt-3">
          <CategoryFilterPanel
            selection={categorySelection}
            options={categoryOptions}
            loading={!categories && !categoriesError}
            error={categoriesError}
            onChange={setCategorySelection}
            onDone={() => setCategoriesOpen(false)}
          />
        </div>
      ) : null}

      {catalogueError && liveId ? (
        <p role="alert" className="px-4 pt-3 text-sm text-danger">
          {catalogueError}
        </p>
      ) : null}

      <div className="flex min-h-0 flex-1 flex-col p-4">
        {legacyId ? (
          <LegacyReportView id={legacyId} />
        ) : nothingTicked ? (
          <p data-category-pick-one className="text-sm text-content-muted">
            {t('reports.categoryFilter.pickOne')}
          </p>
        ) : error ? (
          <p role="alert" className="text-sm text-danger">
            {error}
          </p>
        ) : loading && !table ? (
          <p className="text-sm text-content-muted">{t('common.loading')}</p>
        ) : table && appliedRange ? (
          <ReportTableView report={table} range={appliedRange} categoryIds={requestCategoryIds} />
        ) : (
          <p className="text-sm text-content-muted">{t('common.loading')}</p>
        )}
      </div>
    </div>
  );
}
